// Login — mobile number + 4-digit PIN. On success, if this looks like a new
// device we send a WhatsApp OTP and route to verification (mirrors the Flutter
// single-device flow).
//
// The review test number (1234567890) takes ANY password and is created on
// first use — same exemption the mobile shell applies, from the same module, so
// the two shells cannot drift on which credentials a reviewer needs.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, CircularProgress, Alert,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import { login, sendOtp } from '../../../core/api/api';
import { isTestMobile, resolveTestAccount } from '../../../core/auth/testAccount';

export default function LoginScreen() {
  const navigate = useNavigate();
  const { setSession } = useApp();
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doLogin = async () => {
    setError('');
    // The test number carries no PIN requirement — see core/auth/testAccount.js.
    if (!isTestMobile(mobile) && (mobile.trim().length < 10 || pin.trim().length < 4)) {
      setError('Enter a valid mobile number and 4-digit PIN.');
      return;
    }
    setLoading(true);
    try {
      if (isTestMobile(mobile)) {
        const testRows = await resolveTestAccount(pin.trim());
        if (testRows) {
          setSession(testRows);
          navigate('/home', { replace: true });
        } else {
          setError('Test account is unavailable on the server.');
        }
        return;
      }
      const res = await login(mobile.trim(), pin.trim());
      const arr = res.data;
      if (Array.isArray(arr) && arr.length > 0 && arr[0].UserId) {
        // Persist the entered credentials on the stored record so a later
        // best-effort session refresh always has usable mobile + PIN.
        const merged = [{ ...arr[0], MobileNumber: mobile.trim(), Password: pin.trim() }, ...arr.slice(1)];
        setSession(merged);
        navigate('/home', { replace: true });
      } else {
        setError('Invalid mobile number or PIN.');
      }
    } catch (e) {
      setError('Login failed. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const forgotViaOtp = async () => {
    if (mobile.trim().length < 10) {
      setError('Enter your mobile number first.');
      return;
    }
    setLoading(true);
    try {
      const res = await sendOtp(mobile.trim());
      const otp = Array.isArray(res.data) && res.data[0] ? res.data[0].Otp : null;
      navigate('/otp', { state: { mobile: mobile.trim(), otp } });
    } catch (e) {
      setError('Could not send OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', p: 3, gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h4" fontWeight={700} color="text.primary">
        Smart Life India
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One Stop Solution for the Smartest living
      </Typography>

      {error && <Alert severity="error" sx={{ width: '100%', maxWidth: 360 }}>{error}</Alert>}

      <TextField
        label="Mobile Number" value={mobile}
        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
        sx={{ width: '100%', maxWidth: 360 }}
      />
      <TextField
        label="PIN" type="password" value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
        sx={{ width: '100%', maxWidth: 360 }}
        onKeyDown={(e) => e.key === 'Enter' && doLogin()}
      />

      <Button
        variant="contained" onClick={doLogin} disabled={loading}
        sx={{ width: '100%', maxWidth: 360, py: 1.3 }}
      >
        {loading ? <CircularProgress size={22} /> : 'LOGIN'}
      </Button>
      <Button onClick={forgotViaOtp} disabled={loading} sx={{ color: 'text.secondary' }}>
        Login with WhatsApp OTP
      </Button>
    </Box>
  );
}
